import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { Switch, FormControlLabel, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate, usePermissions } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { AddFilterAction, SelectInput } from '~/components';
import { ApprovalCaseClaimed, ApprovalCaseFilters } from '~/api/approvals';
import { Moment } from '~/types';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';
import {
  ActionTypeFilterInput,
  ObjectTypeFilterInput,
  ObjectIdFilterInput,
  RequestedByIdFilterInput,
} from './ApprovalFilterInputs';

const resource = 'approvals/queue';

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.approvals.list.filters.${key}`);
  const { permissions } = usePermissions();
  const canSeeAllQueue =
    permissions?.includes('approvalCase.list.pending.all') ||
    permissions?.includes('approvalCase.admin');

  const filters = useSelector(selectors.getFilters<ApprovalCaseFilters>(resource));
  const [localValues, setLocalValues] = useState<ApprovalCaseFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>(
    Object.keys(filters).filter((k) => !!(filters as any)[k])
  );

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    debouncedDispatchFilterChange(inputType, data);
  };

  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({ ...localValues, [name]: value });
    debouncedDispatchFilterChange(name, value);
  };

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(filterChange({ resource, filters: { withAllQueue: false } }));
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  const hideFilter = (filter: string) => {
    dispatchFilterChange(filter, '');
    if (filter === 'objectType') {
      dispatchFilterChange('objectId', '');
      setAddedFilters(addedFilters.filter((f) => f !== filter && f !== 'objectId'));
    } else {
      setAddedFilters(addedFilters.filter((f) => f !== filter));
    }
  };

  const handleObjectTypeChange = (e: any) => {
    const { value } = e.target;
    setLocalValues({ ...localValues, objectType: value, objectId: undefined });
    dispatchFilterChange('objectType', value);
    dispatchFilterChange('objectId', '');
  };

  const allFilters = ['actionType', 'claimed', 'objectType', 'objectId', 'requestedById'];

  const claimedOptions: { value: ApprovalCaseClaimed; text: string }[] = [
    { value: 'ME', text: t('claimedOptions.ME') },
    { value: 'UNCLAIMED', text: t('claimedOptions.UNCLAIMED') },
    { value: 'OTHER', text: t('claimedOptions.OTHER') },
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'actionType':
        return (
          <ActionTypeFilterInput
            value={localValues.actionType}
            onChange={(e: any) => {
              const { value } = e.target;
              setLocalValues({ ...localValues, actionType: value });
              dispatchFilterChange('actionType', value);
            }}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'claimed':
        return (
          <SelectInput<ApprovalCaseClaimed | undefined>
            label={t('claimed')}
            name='claimed'
            value={localValues.claimed}
            options={claimedOptions}
            onChange={(e: any) => {
              const { value } = e.target;
              setLocalValues({ ...localValues, claimed: value });
              dispatchFilterChange('claimed', value);
            }}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'objectType':
        return (
          <ObjectTypeFilterInput
            value={localValues.objectType}
            onChange={handleObjectTypeChange}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'objectId':
        return (
          <ObjectIdFilterInput
            value={localValues.objectId}
            onChange={debouncedHandleInput}
            disabled={!localValues.objectType}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'requestedById':
        return (
          <RequestedByIdFilterInput
            value={localValues.requestedById}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (!component) return null;
      return (
        <div className={listFilterClasses.flexContainer} key={filter}>
          {component}
          <IconButton
            aria-label='delete'
            className={listFilterClasses.deleteIcon}
            onClick={() => hideFilter(filter)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      );
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('submittedFrom')}
                value={localValues.submittedFrom ?? null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'submittedFrom')}
              />
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('submittedTo')}
                value={localValues.submittedTo ?? null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'submittedTo')}
              />
              {renderAddedFilters()}
              {canSeeAllQueue && (
                <FormControlLabel
                  className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
                  control={
                    <Switch
                      checked={localValues.withAllQueue ?? false}
                      onChange={(e) => {
                        const value = e.target.checked;
                        setLocalValues({ ...localValues, withAllQueue: value });
                        dispatch(filterChange({ resource, filters: { withAllQueue: value } }));
                      }}
                    />
                  }
                  label={t('withAllQueue')}
                />
              )}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: t(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const useStyles = makeStyles(() => ({
  leftSide: {
    maxWidth: 'calc(100% - 100px)',
  },
  rightSide: { minWidth: 100, textAlign: 'right' },
}));

export default Filters;
